// Singleton tracker for inbox unread counts (HEXA + External).
// Consumers (Dock badge, Mail sidebar, TopBar) subscribe to receive live updates.
//
// Updates fire on:
//   • initial fetch
//   • chatBus "mail:new" events (WebSocket broadcast from backend send/sync)
//   • manual refresh() calls
//   • 30 s polling fallback (in case socket drops)

import { mailUnreadApi } from "./api";
import { chatBus } from "./chatSocket";

class MailUnreadBus {
  constructor() {
    this.counts = { hexa: 0, external: 0, total: 0 };
    this.listeners = new Set();
    this.timer = null;
    this.connected = false;
    this.lastEvent = null;
  }

  subscribe(fn) {
    this.listeners.add(fn);
    // Emit current snapshot immediately so consumers paint without waiting.
    try { fn(this.counts, this.lastEvent); } catch { /* noop */ }
    return () => this.listeners.delete(fn);
  }

  _emit(evt = null) {
    this.lastEvent = evt;
    for (const l of Array.from(this.listeners)) {
      try { l(this.counts, evt); } catch { /* noop */ }
    }
  }

  async refresh() {
    try {
      const c = await mailUnreadApi.counts();
      this.counts = {
        hexa: Number(c.hexa || 0),
        external: Number(c.external || 0),
        total: Number(c.total || 0),
      };
      this._emit();
    } catch { /* ignore — auth-gated */ }
  }

  start() {
    if (this.connected) return;
    this.connected = true;
    this.refresh();
    // 30 s polling fallback (only matters if WS drops).
    this.timer = setInterval(() => this.refresh(), 30000);
    // React to WS broadcasts pushed by the backend whenever a new inbox
    // message lands for THIS user (send_message / external_sync).
    this._unsub = chatBus.subscribe((evt) => {
      if (evt && evt.type === "mail:new") {
        this.refresh().then(() => this._emit(evt));
      }
    });
  }

  stop() {
    this.connected = false;
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
    if (this._unsub) { this._unsub(); this._unsub = null; }
    this.counts = { hexa: 0, external: 0, total: 0 };
    this._emit();
  }
}

export const mailUnread = new MailUnreadBus();
